/**
 * Cliente Core Service
 *
 * Handles core CRUD operations for Cliente (Distributor) entity,
 * data formatting helpers, SMTP test, export to Excel, and
 * interval validation (Epic 46).
 */
import apiClient from '../apiClient';
import { limparDocumento } from '../../lib/utils';
import type {
  Cliente,
  ClienteInput,
  ProdutoCliente,
  ProdutoClienteInput,
  ProdutoClienteGlobal,
  SMTPTestRequest,
  SMTPTestResponse,
  ValidarIntervaloResponse,
  ValidarIntervaloMultiplosResponse,
} from './clienteTypes';

class ClienteCoreService {
  private readonly baseUrl = '/clientes';

  /**
   * Limpa dados antes de enviar para API (remove formatação de telefone/cnpj/cep)
   */
  private cleanDataForApi(data: ClienteInput): ClienteInput {
    return {
      ...data,
      telefone: data.telefone ? limparDocumento(data.telefone) : data.telefone,
      cnpj: data.cnpj ? limparDocumento(data.cnpj) : data.cnpj,
      cep: data.cep ? limparDocumento(data.cep) : data.cep,
    };
  }

  // ============================================================================
  // CRUD CLIENTES
  // ============================================================================

  async listClientes(): Promise<Cliente[]> {
    const response = await apiClient.get<Cliente[]>(this.baseUrl);
    return response.data;
  }

  async getCliente(id: number): Promise<Cliente> {
    const response = await apiClient.get<Cliente>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async createCliente(data: ClienteInput): Promise<Cliente> {
    const cleanData = this.cleanDataForApi(data);
    const response = await apiClient.post<Cliente>(this.baseUrl, cleanData);
    return response.data;
  }

  async updateCliente(id: number, data: ClienteInput): Promise<Cliente> {
    const cleanData = this.cleanDataForApi(data);
    const response = await apiClient.put<Cliente>(`${this.baseUrl}/${id}`, cleanData);
    return response.data;
  }

  /** Remove cliente (soft delete - marca como inativo) */
  async deleteCliente(id: number): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>(
      `${this.baseUrl}/${id}`
    );
    return response.data;
  }

  async reactivateCliente(id: number): Promise<Cliente> {
    const response = await apiClient.post<Cliente>(`${this.baseUrl}/${id}/reativar`);
    return response.data;
  }

  // ============================================================================
  // FORMAT HELPERS
  // ============================================================================

  validateCNPJ(cnpj: string): boolean {
    const clean = cnpj.replace(/\D/g, '');
    return clean.length === 14;
  }

  formatCNPJ(cnpj: string): string {
    const clean = cnpj.replace(/\D/g, '');
    if (clean.length !== 14) return cnpj;
    return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

  formatTelefone(telefone: string): string {
    const clean = telefone.replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    } else if (clean.length === 10) {
      return clean.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    return telefone;
  }

  // ============================================================================
  // PRODUTOS DO CLIENTE (Sprint 3 REDESIGN)
  // ============================================================================

  async listProdutosCliente(clienteId: number, search?: string): Promise<ProdutoCliente[]> {
    const response = await apiClient.get<{
      items: ProdutoCliente[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>(
      `${this.baseUrl}/${clienteId}/produtos-cliente`,
      { params: search ? { search, page_size: 100000 } : { page_size: 100000 } }
    );
    return response.data.items;
  }

  async createProdutoCliente(
    clienteId: number,
    data: ProdutoClienteInput
  ): Promise<ProdutoCliente> {
    const response = await apiClient.post<ProdutoCliente>(
      `${this.baseUrl}/${clienteId}/produtos-cliente`,
      data
    );
    return response.data;
  }

  async updateProdutoCliente(
    clienteId: number,
    produtoId: number,
    data: ProdutoClienteInput
  ): Promise<ProdutoCliente> {
    const response = await apiClient.put<ProdutoCliente>(
      `${this.baseUrl}/${clienteId}/produtos-cliente/${produtoId}`,
      data
    );
    return response.data;
  }

  async deleteProdutoCliente(
    clienteId: number,
    produtoId: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>(
      `${this.baseUrl}/${clienteId}/produtos-cliente/${produtoId}`
    );
    return response.data;
  }

  // ============================================================================
  // PRODUTOS GLOBAL VIEW (Sprint 3.5)
  // ============================================================================

  async listAllProdutosCliente(clienteId?: number): Promise<ProdutoClienteGlobal[]> {
    const params = { cliente_id: clienteId || 0 };
    const response = await apiClient.get<ProdutoClienteGlobal[]>(
      '/todos-produtos-cliente',
      { params }
    );
    return response.data;
  }

  // ============================================================================
  // EXPORTAÇÃO PARA EXCEL
  // ============================================================================

  async exportExcel(): Promise<void> {
    const response = await apiClient.get('/clientes/export-excel', {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.setAttribute('download', `clientes_export_${timestamp}.xlsx`);

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  }

  // ============================================================================
  // SMTP
  // ============================================================================

  async testSMTP(data: SMTPTestRequest): Promise<SMTPTestResponse> {
    const response = await apiClient.post<SMTPTestResponse>('/smtp/test', data);
    return response.data;
  }

  // ============================================================================
  // EPIC 46: VALIDAÇÃO DE INTERVALO DE AGENDAMENTO
  // ============================================================================

  async validarIntervaloAgendamento(
    clienteId: number,
    hospitalId: number,
    dataPrevista: string,
    jobIdExcluir?: number
  ): Promise<ValidarIntervaloResponse> {
    const response = await apiClient.post<ValidarIntervaloResponse>(
      `${this.baseUrl}/${clienteId}/validar-intervalo-agendamento`,
      {
        hospital_id: hospitalId,
        data_prevista: dataPrevista,
        job_id_excluir: jobIdExcluir
      }
    );
    return response.data;
  }

  async validarIntervaloMultiplosClientes(
    clienteIds: number[],
    hospitalId: number,
    dataPrevista: string,
    jobIdExcluir?: number
  ): Promise<ValidarIntervaloMultiplosResponse> {
    const response = await apiClient.post<ValidarIntervaloMultiplosResponse>(
      `${this.baseUrl}/validar-intervalo-agendamento/multiplos`,
      {
        cliente_ids: clienteIds,
        hospital_id: hospitalId,
        data_prevista: dataPrevista,
        job_id_excluir: jobIdExcluir
      }
    );
    return response.data;
  }
}

export default new ClienteCoreService();
